import { useEditor, EditorContent, Editor } from '@tiptap/react'
import { BubbleMenu } from '@tiptap/react/menus'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import Image from '@tiptap/extension-image'
import { forwardRef, useImperativeHandle, useEffect, useRef, useCallback } from 'react'

import BubbleMenuToolbar from './BubbleMenuToolbar'
import { VariableExtension } from './VariableExtension'

interface ChatEditorProps {
  value: string
  onChange: (value: string) => void
  onTextChange?: (text: string) => void
  onSubmit?: () => void
  placeholder?: string
  disabled?: boolean
  channel?: 'chat' | 'email' | 'whatsapp'
  onMacroTrigger?: (query: string, position: number) => void
  onMacroClose?: () => void
  onPasteFiles?: (files: File[]) => void
}

export interface ChatEditorRef {
  editor: Editor | null
  focus: () => void
  insertHtml: (html: string) => void
  clear: () => void
  getTextContent: () => string
}

function stripProseMirrorArtifacts(html: string): string {
  return html
    // Keep <br> for empty paragraphs so intentional blank lines survive after sending.
    .replace(/<br\s+class="ProseMirror-trailingBreak"\s*\/?>/g, '<br>')
    .replace(/<br\s+class="ProseMirror-separator"\s*\/?>/g, '')
    // Some Enter-created empty paragraphs can serialize as <p></p>; keep them visible.
    .replace(/<p>\s*<\/p>/g, '<p><br></p>')
}

const ChatEditor = forwardRef<ChatEditorRef, ChatEditorProps>(
  (
    {
      value,
      onChange,
      onTextChange,
      onSubmit,
      placeholder = 'Digite sua mensagem...',
      disabled = false,
      channel,
      onMacroTrigger,
      onMacroClose,
      onPasteFiles,
    },
    ref
  ) => {
    const initialValueRef = useRef(value)
    const macroStartPosRef = useRef<number | null>(null)

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: { levels: [1, 2] },
          blockquote: false, // Disable for chat to keep it simpler
          link: false,
        }),
        Link.configure({
          openOnClick: false,
          HTMLAttributes: {
            target: '_blank',
            rel: 'noopener noreferrer',
          },
        }),
        Placeholder.configure({
          placeholder,
        }),
        Image.configure({
          inline: true,
        }),
        VariableExtension,
      ],
      content: value || '',
      editable: !disabled,
      onUpdate: ({ editor }) => {
        const html = stripProseMirrorArtifacts(editor.getHTML())
        onChange(html)
        onTextChange?.(editor.getText())

        // Check for macro trigger (/)
        checkMacroTrigger(editor)
      },
      editorProps: {
        attributes: {
          class: 'prose prose-sm max-w-none prose-p:my-0 prose-p:leading-normal prose-img:my-0 focus:outline-none px-3 py-2 min-h-[100px]',
        },
        handlePaste: (_view, event) => {
          if (!onPasteFiles) return false

          const items = event.clipboardData?.items
          if (!items) return false

          const imageFiles: File[] = []
          for (const item of Array.from(items)) {
            if (item.type.startsWith('image/')) {
              const file = item.getAsFile()
              if (file) imageFiles.push(file)
            }
          }

          if (imageFiles.length > 0) {
            onPasteFiles(imageFiles)
            return true
          }

          return false
        },
        handleKeyDown: (_view, event) => {
          if (event.defaultPrevented) return false

          // Submit on Cmd/Ctrl + Enter
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault()
            onSubmit?.()
            return true
          }

          // Close macro picker on Escape
          if (event.key === 'Escape') {
            if (macroStartPosRef.current !== null) {
              macroStartPosRef.current = null
              onMacroClose?.()
              return true
            }
          }

          return false
        },
      },
    })

    // Check if we should trigger macro picker
    const checkMacroTrigger = useCallback(
      (editor: Editor) => {
        if (!onMacroTrigger) return

        const { from } = editor.state.selection

        // Resolve cursor position to get paragraph context
        // Use textBetween to represent hardBreaks (Shift+Enter) as \n
        const $pos = editor.state.doc.resolve(from)
        const textBeforeCursor = $pos.parent.textBetween(0, $pos.parentOffset, '', '\n')

        // Look for / at start of paragraph or after whitespace
        const macroMatch = textBeforeCursor.match(/(?:^|\s)\/([^\s]*)$/)

        if (macroMatch) {
          const query = macroMatch[1]
          macroStartPosRef.current = from - query.length - 1 // Position of /
          onMacroTrigger(query, macroStartPosRef.current)
        } else if (macroStartPosRef.current !== null) {
          macroStartPosRef.current = null
          onMacroClose?.()
        }
      },
      [onMacroTrigger, onMacroClose]
    )

    // Expose methods via ref
    useImperativeHandle(
      ref,
      () => ({
        editor,
        focus: () => editor?.commands.focus(),
        insertHtml: (html: string) => {
          if (!editor) return

          // Clean up HTML for better Tiptap compatibility
          const cleanHtml = stripProseMirrorArtifacts(html)
            // Remove empty paragraphs
            .replace(/<p><\/p>/g, '')
            .replace(/<p>\s*<\/p>/g, '')
            .replace(/<p>\s*<br[^>]*>\s*<\/p>/g, '')
            // Wrap standalone images in paragraphs (Tiptap handles them better this way)
            .replace(/(<\/(?:p|h[1-6])>)(<img\s)/g, '$1<p>$2')
            .replace(/(<img[^>]*>)(<(?:p|h[1-6]))/g, '$1</p>$2')
            .replace(/(<img[^>]*>)$/g, '$1</p>')

          // If we're inserting from macro picker, replace the /query
          if (macroStartPosRef.current !== null) {
            const { state } = editor
            const { from } = state.selection
            const macroStart = macroStartPosRef.current

            const $macroPos = state.doc.resolve(macroStart)
            const isAtParagraphStart = $macroPos.parentOffset === 0
            const paragraphContent = $macroPos.parent.textContent
            const queryText = state.doc.textBetween(macroStart, from, '')
            const isOnlyContent = paragraphContent === queryText
            const paragraphStart = macroStart - 1

            if (isAtParagraphStart && isOnlyContent && paragraphStart === 0) {
              // The /query is the only content in the document
              editor.commands.setContent(cleanHtml)
            } else {
              // Delete the /query (or the entire paragraph if it only contains the query)
              const deleteFrom = isAtParagraphStart && isOnlyContent ? paragraphStart : macroStart
              const deleteTo = isAtParagraphStart && isOnlyContent ? $macroPos.end() + 1 : from

              editor
                .chain()
                .focus()
                .command(({ tr }) => {
                  tr.delete(deleteFrom, deleteTo)
                  return true
                })
                .insertContent(cleanHtml)
                .run()
            }

            macroStartPosRef.current = null
          } else {
            editor.chain().focus().insertContent(cleanHtml).run()
          }
        },
        clear: () => {
          editor?.commands.clearContent()
          macroStartPosRef.current = null
        },
        getTextContent: () => editor?.getText() || '',
      }),
      [editor]
    )

    // Update editor when value changes externally
    useEffect(() => {
      if (editor && !editor.isFocused && value !== initialValueRef.current) {
        const currentHtml = editor.getHTML()
        if (currentHtml !== value) {
          editor.commands.setContent(value || '')
          initialValueRef.current = value
        }
      }
    }, [value, editor])

    // Update editable state
    useEffect(() => {
      if (editor) {
        editor.setEditable(!disabled)
      }
    }, [editor, disabled])

    return (
      <div className="relative chat-editor">
        <div className="bg-gray-50 border border-gray-200 rounded-xl focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent overflow-hidden">
          <EditorContent
            editor={editor}
            className="[&_.ProseMirror]:min-h-[100px] [&_.ProseMirror]:max-h-[300px] [&_.ProseMirror]:overflow-y-auto [&_.ProseMirror]:outline-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-gray-400 [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0 [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none [&_.ProseMirror_h1]:text-xl [&_.ProseMirror_h1]:font-bold [&_.ProseMirror_h1]:mb-1 [&_.ProseMirror_h2]:text-lg [&_.ProseMirror_h2]:font-semibold [&_.ProseMirror_h2]:mb-1 [&_.ProseMirror_code]:bg-gray-200 [&_.ProseMirror_code]:px-1 [&_.ProseMirror_code]:rounded [&_.ProseMirror_code]:font-mono [&_.ProseMirror_code]:text-sm [&_.ProseMirror_pre]:bg-gray-900 [&_.ProseMirror_pre]:text-gray-100 [&_.ProseMirror_pre]:p-3 [&_.ProseMirror_pre]:rounded-lg [&_.ProseMirror_pre]:overflow-x-auto [&_.ProseMirror_pre]:text-sm [&_.ProseMirror_a]:text-blue-600 [&_.ProseMirror_a]:underline [&_.ProseMirror_img]:max-w-full [&_.ProseMirror_img]:h-auto [&_.ProseMirror_img]:rounded-lg [&_.ProseMirror_img]:my-0"
          />

          {/* Bubble Menu */}
          {editor && (
            <BubbleMenu
              editor={editor}
              shouldShow={({ editor: bubbleEditor, state }) => {
                const { from, to } = state.selection
                const text = state.doc.textBetween(from, to, '')
                return text.length > 0 && !bubbleEditor.isActive('image')
              }}
            >
              <BubbleMenuToolbar editor={editor} channel={channel} />
            </BubbleMenu>
          )}
        </div>
      </div>
    )
  }
)

ChatEditor.displayName = 'ChatEditor'
export default ChatEditor
